import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Icon from '../../../components/AppIcon';

const TaxCalculationForm = ({ onAddTransaction }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [formData, setFormData] = useState({
    type: 'buy',
    asset: '',
    assetType: 'crypto',
    quantity: '',
    price: '',
    fee: '',
    date: '',
    notes: ''
  });

  const transactionTypes = [
    { value: 'buy', label: 'Buy' },
    { value: 'sell', label: 'Sell' },
    { value: 'trade', label: 'Trade' },
    { value: 'mining', label: 'Mining' },
    { value: 'staking', label: 'Staking' },
    { value: 'airdrop', label: 'Airdrop' },
    { value: 'fork', label: 'Fork' }
  ];

  const assetTypes = [
    { value: 'crypto', label: 'Cryptocurrency' },
    { value: 'stock', label: 'Stock' },
    { value: 'etf', label: 'ETF' },
    { value: 'options', label: 'Options' },
    { value: 'futures', label: 'Futures' }
  ];

  const cryptoAssets = [
    { value: 'BTC', label: 'Bitcoin (BTC)' },
    { value: 'ETH', label: 'Ethereum (ETH)' },
    { value: 'ADA', label: 'Cardano (ADA)' },
    { value: 'SOL', label: 'Solana (SOL)' },
    { value: 'DOT', label: 'Polkadot (DOT)' },
    { value: 'LINK', label: 'Chainlink (LINK)' },
    { value: 'MATIC', label: 'Polygon (MATIC)' },
    { value: 'AVAX', label: 'Avalanche (AVAX)' }
  ];

  const stockAssets = [
    { value: 'AAPL', label: 'Apple Inc. (AAPL)' },
    { value: 'MSFT', label: 'Microsoft (MSFT)' },
    { value: 'GOOGL', label: 'Alphabet (GOOGL)' },
    { value: 'TSLA', label: 'Tesla (TSLA)' },
    { value: 'AMZN', label: 'Amazon (AMZN)' },
    { value: 'NVDA', label: 'NVIDIA (NVDA)' },
    { value: 'META', label: 'Meta (META)' },
    { value: 'NFLX', label: 'Netflix (NFLX)' }
  ];

  const handleFormChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    
    if (!formData?.asset || !formData?.quantity || !formData?.price || !formData?.date) {
      return;
    }

    const transaction = {
      ...formData,
      quantity: parseFloat(formData?.quantity),
      price: parseFloat(formData?.price),
      fee: parseFloat(formData?.fee || 0),
      totalValue: (parseFloat(formData?.quantity) * parseFloat(formData?.price)) + 
                   parseFloat(formData?.fee || 0),
      costBasis: formData?.type === 'buy' ? 
        (parseFloat(formData?.quantity) * parseFloat(formData?.price)) + parseFloat(formData?.fee || 0) :
        0
    };

    onAddTransaction(transaction);
    
    // Reset form
    setFormData({
      type: 'buy',
      asset: '',
      assetType: 'crypto',
      quantity: '',
      price: '',
      fee: '',
      date: '',
      notes: ''
    });
    setIsExpanded(false);
  };

  const getAssetOptions = () => {
    return formData?.assetType === 'crypto' ? cryptoAssets : stockAssets;
  };

  return (
    <div className="bg-card border border-border rounded-lg">
      <div className="p-6">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-card-foreground">Add Transaction</h2>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setIsExpanded(!isExpanded)}
          >
            <Icon 
              name={isExpanded ? 'ChevronUp' : 'ChevronDown'} 
              size={20} 
              className="text-muted-foreground" 
            />
          </Button>
        </div>

        {!isExpanded && (
          <p className="text-sm text-muted-foreground mt-2">
            Click to add a new trading transaction for tax calculation
          </p>
        )}
      </div>

      {isExpanded && (
        <div className="px-6 pb-6">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Select
                label="Transaction Type"
                placeholder="Select transaction type"
                options={transactionTypes}
                value={formData?.type}
                onChange={(value) => handleFormChange('type', value)}
                required
              />

              <Select
                label="Asset Type"
                placeholder="Select asset type"
                options={assetTypes}
                value={formData?.assetType}
                onChange={(value) => {
                  handleFormChange('assetType', value);
                  handleFormChange('asset', ''); // Reset asset when type changes
                }}
                required
              />

              <Select
                label="Asset"
                placeholder={`Select ${formData?.assetType}`}
                options={getAssetOptions()}
                value={formData?.asset}
                onChange={(value) => handleFormChange('asset', value)}
                required
              />

              <Input
                label="Date"
                type="date"
                value={formData?.date}
                onChange={(e) => handleFormChange('date', e?.target?.value)}
                required
              />

              <Input
                label="Quantity"
                type="number"
                step="0.00000001"
                placeholder="0.00000000"
                value={formData?.quantity}
                onChange={(e) => handleFormChange('quantity', e?.target?.value)}
                required
              />

              <Input
                label="Price per Unit (USD)"
                type="number"
                step="0.01"
                placeholder="0.00"
                value={formData?.price}
                onChange={(e) => handleFormChange('price', e?.target?.value)}
                required
              />

              <Input
                label="Fees (Optional)"
                type="number"
                step="0.01"
                placeholder="0.00"
                value={formData?.fee}
                onChange={(e) => handleFormChange('fee', e?.target?.value)}
              />
            </div>

            <Input
              label="Notes (Optional)"
              placeholder="Add any additional notes about this transaction"
              value={formData?.notes}
              onChange={(e) => handleFormChange('notes', e?.target?.value)}
            />

            <div className="flex space-x-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsExpanded(false)}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="default"
                className="flex-1"
                disabled={!formData?.asset || !formData?.quantity || !formData?.price || !formData?.date}
              >
                Add Transaction
              </Button>
            </div>
          </form>

          {/* Transaction Type Info */}
          <div className="mt-6 p-4 bg-muted/50 rounded-md">
            <div className="flex items-start space-x-2">
              <Icon name="Info" size={16} className="text-primary mt-0.5" />
              <div>
                <p className="text-sm font-medium text-card-foreground mb-1">Transaction Types</p>
                <div className="text-xs text-muted-foreground space-y-1">
                  <p><strong>Buy/Sell:</strong> Standard purchase or sale transactions</p>
                  <p><strong>Mining:</strong> Cryptocurrency received through mining (taxable as income)</p>
                  <p><strong>Staking:</strong> Rewards from staking activities (taxable as income)</p>
                  <p><strong>Airdrop:</strong> Free tokens received (taxable as income at fair market value)</p>
                  <p><strong>Fork:</strong> New tokens received from blockchain forks</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TaxCalculationForm;